import styles from './PaymentPlan.module.scss';
import { Selectmenu, Textbox, DatePicker, Button, Checkbox, Popup } from 'components/generic';
import OverviewContext from 'store/overview-context';
import { useContext, useEffect, useState, useCallback } from 'react';
import amountFormatter from 'utils/amountFormatter.js';
import moment from 'moment';
import { arrangementFrequency, remainderAmountOptions } from 'config/config';
import { getValueById } from 'utils/dropdownUtils';
import { requestValidator } from 'actions/actions';
import { cloneDeep } from 'lodash';

const PaymentPlan = props => {
	const overviewContext = useContext(OverviewContext);
	const { paymentPlanObj, setPaymentPlanObj } = overviewContext;
	const [frequencyList, setFrequencyList] = useState([]);
	const [showtermsConditions, setTermsConditions] = useState(false);
	const [disabled, setDisabled] = useState(false);
	const [editState, setEditState] = useState(false);

	const getLang = key => {
		return overviewContext?.langObj?.content?.[key] || '';
	};

	const getLangDropdown = list => {
		const obj = cloneDeep(list);
		obj.forEach(elm => (elm.value = getLang(elm?.key?.toUpperCase())));
		return obj;
	};

	// setting form data
	const dataService = (data, key) => {
		setPaymentPlanObj(prevState => {
			const state = cloneDeep(prevState);
			state[key] = data;
			return state;
		});
		!['pp_remainderAmtType', 'pp_reason', 'acceptTerms']?.includes(key) && setPaymentPlanObj(prevState => ({ ...prevState, pp_partPayments: [], pp_remainderAmtType: null }));
	};

	// payment plan submit api call
	const submitPaymentPlan = () => {
		const obj = { ...overviewContext?.createObj, ...paymentPlanObj };
		obj.pp_paymtAmt = +obj.pp_paymtAmt;
		props.onSubmitPaymentPlan(obj);
	};

	// adjusting remainder amount
	const adjustRemainder = useCallback(
		(arrangementList, occuranceAmount, occuranceCount, totalAmt) => {
			const remainder = parseFloat((totalAmt - occuranceAmount * occuranceCount).toFixed(2));
			if (arrangementList?.length > 0 && +remainder > 0) {
				const obj = {
					start_of_payment: () => {
						arrangementList[0].pp_amount += +remainder.toFixed(2);
					},
					end_of_payment: () => {
						arrangementList[arrangementList.length - 1].pp_amount += +remainder.toFixed(2);
					},
					default: () => { }
				};
				(obj[paymentPlanObj?.pp_remainderAmtType] || obj['default'])();
			}
			setPaymentPlanObj(prevState => ({ ...prevState, pp_partPayments: arrangementList }));
		},
		[overviewContext]
	);

	// setting arrangment dates part 2
	const setDates = useCallback(
		params => {
			let date = '';
			const arrangementList = [];
			new Array(params?.totalOccurances)?.fill(0)?.forEach((el, i) => {
				const count = i + 1;
				if (i === 0) {
					date = moment(params?.fromDate).format('YYYY-MM-DD');
					arrangementList.push({ pp_paymentNumber: count, pp_amount: params?.occuranceAmt, pp_date: date });
				} else {
					if (params?.type === 'days') {
						date = moment(params?.fromDate, 'YYYY-MM-DD')
							.add(params?.number * i, 'days')
							.format('YYYY-MM-DD');
					} else {
						if (moment(params?.fromDate, 'YYYY-MM-DD').format('YYYY-MM-DD') === moment(params?.fromDate, 'YYYY-MM-DD').endOf('month').format('YYYY-MM-DD')) {
							date = moment(params?.fromDate, 'YYYY-MM-DD')
								.add(params?.number * i, 'M')
								.endOf('month')
								.format('YYYY-MM-DD');
						} else {
							date = moment(params?.fromDate, 'YYYY-MM-DD')
								.add(params?.number * i, 'M')
								.format('YYYY-MM-DD');
						}
					}
					arrangementList.push({ pp_paymentNumber: count, pp_amount: params?.occuranceAmt, pp_date: date });
				}
			});
			adjustRemainder(arrangementList, params?.occuranceAmt, params?.totalOccurances, params?.totalAmt);
		},
		[adjustRemainder]
	);

	// setting arrangment dates part 1
	const arragementDates = useCallback(
		params => {
			const frequencyObj = arrangementFrequency?.find(el => el?.key === params.frequency);
			const obj = { ...params, type: frequencyObj?.type, number: frequencyObj?.number };
			paymentPlanObj?.pp_paymtAmt && frequencyObj && setDates(obj);
		},
		[setDates]
	);

	// calculate arrangement installments and dates
	const calculateArrangement = useCallback(() => {
		setPaymentPlanObj(prevState => ({ ...prevState, pp_partPayments: [] }));
		const occuranceAmt = +paymentPlanObj?.pp_paymtAmt;
		const totalAmt = +overviewContext?.overviewData?.bd_dueAmt;
		const totalOccurances = Math.floor(totalAmt / occuranceAmt);
		setPaymentPlanObj(prevState => ({ ...prevState, pp_paymtOccurances: totalOccurances }));
		const obj = { frequency: paymentPlanObj?.pp_frequency, fromDate: paymentPlanObj?.pp_fromDate, totalOccurances, occuranceAmt, totalAmt };
		arragementDates(obj);
	}, [overviewContext, paymentPlanObj, arragementDates]);

	// setting frequency list for dropdown
	useEffect(() => {
		overviewContext && setFrequencyList(overviewContext?.overviewData?.wf_linkConfiguration?.paymentPlanConfiguration?.paymentFrequency?.map(el => ({ key: el, value: el?.toLowerCase() })));
	}, [overviewContext?.overviewData]);

	useEffect(() => {
		const isValid = requestValidator({ ...paymentPlanObj, validatorKey: 'paymentPlanValidations' });
		setDisabled(isValid);
	}, [paymentPlanObj]);

	// calculating arrangement occurances
	useEffect(() => {
		paymentPlanObj?.pp_remainderAmtType && calculateArrangement();
	}, [paymentPlanObj?.pp_remainderAmtType]);

	useEffect(() => {
		setEditState(overviewContext?.overviewData?.wf_linkConfiguration?.paymentPlanConfiguration?.allowEdit ? true : !overviewContext?.overviewData?.paymentPlan?.length);
	}, [overviewContext?.overviewData]);

	// arrangment installments display component
	const displayComponent = paymentPlanObj?.pp_partPayments?.length > 0 && (
		<div className={`${styles.container__display}`}>
			<div className={styles.container__date}>
				<h3>{getLang('STARTS_ON')}</h3>
				<span>{moment(paymentPlanObj?.pp_fromDate).format('dddd Do MMMM, YYYY')}</span>
			</div>
			<div className={styles.container__installments}>
				<h3>{getLang('INSTALLMENTS')}</h3>
				<ul className={styles['scroll-bar']}>
					{paymentPlanObj?.pp_partPayments?.map(el => (
						<li key={el?.pp_paymentNumber}>{`${el?.pp_paymentNumber}. ${amountFormatter({ value: el?.pp_amount, currencyCode: overviewContext?.overviewData?.bd_currencyCode })} - ${moment(el?.pp_date).format('ddd Do MMM, YYYY')}`}</li>
					))}
				</ul>
			</div>
		</div>
	);

	const handleValidation = (e, key) => {
		let amount = +e;
		Number.isNaN(amount) && (amount = 0);
		const minAmt = overviewContext?.overviewData?.wf_linkConfiguration?.paymentPlanConfiguration?.minimumAmount;
		amount < minAmt && (amount = minAmt);
		amount > overviewContext?.overviewData?.bd_dueAmt && (amount = overviewContext?.overviewData?.bd_dueAmt);
		setPaymentPlanObj(prevState => {
			const state = cloneDeep(prevState);
			state[key] = amount;
			return state;
		});
	};

	const templateArray = overviewContext?.overviewData?.wf_linkConfiguration?.paymentPlanConfiguration?.templateContent;
	const templateHtml = templateArray?.length && templateArray?.find(template => template?.templateLanguage?.toUpperCase() === overviewContext?.langObj?.key);

	return (
		<>
			<div className={styles.container}>
				<h2>{amountFormatter({ value: overviewContext?.overviewData?.bd_dueAmt, currencyCode: overviewContext?.overviewData?.bd_currencyCode })}</h2>
				<div className={styles['row']}>
					<div className={styles['col-6']}>
						<DatePicker
							label={getLang('PAYMENT_START')}
							infoContent={getLang('INFO_PAYMENT_START')}
							isDisabled={!editState}
							minDate={new Date()}
							value={paymentPlanObj?.pp_fromDate}
							mandatory
							onChange={data => dataService(data, 'pp_fromDate')}
						/>
					</div>
					<div className={styles['col-6']}>
						<Selectmenu
							label={getLang('PAYMENT_FREQUENCY')}
							disabled={!editState}
							placeholder={getLang('EX_PAYMENT_FREQUENCY')}
							infoContent={getLang('INFO_PAYMENT_FREQUENCY')}
							value={getValueById(paymentPlanObj?.pp_frequency, overviewContext?.langObj?.key !== 'ENGLISH' ? getLangDropdown(frequencyList) : frequencyList)}
							mandatory
							onChange={data => dataService(data, 'pp_frequency')}
							menuOptions={overviewContext?.langObj?.key !== 'ENGLISH' ? getLangDropdown(frequencyList) : frequencyList}
						/>
					</div>
				</div>
				<div className={styles['row']}>
					<div className={styles['col-6']}>
						<Textbox
							placeholder={getLang('EX_AMOUNT')}
							infoContent={getLang('INFO_PAYMENT_START')}
							disabled={!editState}
							onFocusOut={e => handleValidation(e?.target?.value, 'pp_paymtAmt')}
							value={paymentPlanObj?.pp_paymtAmt}
							mandatory
							label={getLang('INFO_INSTALLATION_AMOUNT')}
							onChange={e => dataService(e?.target?.value, 'pp_paymtAmt')}
						/>
					</div>
					<div className={styles['col-6']}>
						<Selectmenu
							label={getLang('ADJUST_REMAINDER_AMOUNT')}
							infoContent={getLang('INFO_ADJUST_REMAINDER_AMOUNT')}
							value={getValueById(paymentPlanObj?.pp_remainderAmtType, overviewContext?.langObj?.key !== 'ENGLISH' ? getLangDropdown(remainderAmountOptions) : remainderAmountOptions)}
							placeholder={getLang('EX_END_PAYMENT')}
							mandatory
							disabled={!editState}
							onChange={data => dataService(data, 'pp_remainderAmtType')}
							menuOptions={overviewContext?.langObj?.key !== 'ENGLISH' ? getLangDropdown(remainderAmountOptions) : remainderAmountOptions}
						/>
					</div>
				</div>
				{displayComponent}
				{overviewContext?.overviewData?.wf_linkConfiguration?.paymentPlanConfiguration?.captureReason && (
					<Textbox
						placeholder={getLang('EX_REASON_PAYMENT_PLAN')}
						value={paymentPlanObj?.pp_reason}
						maxlength={overviewContext?.overviewData?.wf_linkConfiguration?.paymentPlanConfiguration?.maximumCharLimit || 255}
						label={getLang('REASON_PAYMENT_PLAN')}
						infoContent={getLang('INFO_PAYMENT_START')}
						onChange={e => dataService(e?.target?.value, 'pp_reason')}
					/>
				)}
				{editState && (
					<div className={styles.row}>
						<div className={styles['col-12']}>
							<Checkbox content={getLang('I_AGREE')} link={getLang('TERMS_CONDITIONS')} checked={paymentPlanObj?.acceptTerms} onLink={() => setTermsConditions(true)} onClick={e => dataService(e?.target?.checked, 'acceptTerms')} />
						</div>

						<div className={styles['col-12']}>
							<Button primary full label={getLang('CREATE_PAYMENT_PLAN')} disabled={disabled || !paymentPlanObj?.acceptTerms} onSubmit={submitPaymentPlan} />
						</div>
					</div>
				)}
			</div>
			<Popup onClose={() => setTermsConditions(false)} popupState={showtermsConditions}>
				<div className={`${styles.popupContainer} scroll-bar`}>
					<h2>{getLang('TERMS_CONDITIONS')}</h2>
					<div className={`${styles.container__content} scroll-bar`} dangerouslySetInnerHTML={{ __html: templateHtml?.templateContent }}></div>
				</div>
			</Popup>
		</>
	);
};

export default PaymentPlan;
